import React, { useMemo, useRef } from "react";
import { Calculator, Link2, FileSpreadsheet, Eraser, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { computeCost, buildCostCSV, addonKey, isAddonKey, serializeSeats } from "../../lib/cost";
import { formatPrice } from "../../lib/matrix";
import { downloadBlob } from "../../utils/exporters";
import CostLine from "./CostLine";
import AddonPicker from "./AddonPicker";
import ShareMenu from "./ShareMenu";
import QuoteCard from "./QuoteCard";

export default function CostSheet({ open, onOpenChange, plans, addons, groups, visiblePlans, seats, onSeatsChange, priceMode, pricing }) {
  const cardRef = useRef(null);
  const symbol = pricing?.symbol || "$";
  const cost = useMemo(() => computeCost({ seats, plans, addons, priceMode }), [seats, plans, addons, priceMode]);
  const lineByKey = Object.fromEntries(cost.lines.map((l) => [l.key, l]));
  const warnByKey = Object.fromEntries(cost.warnings.map((w) => [w.key, w.text]));

  // Every license shown in the matrix, plus anything that already has seats (e.g. from a shared link)
  const scope = new Set([...(visiblePlans || []), ...Object.keys(seats).filter((k) => !isAddonKey(k))]);
  const sections = groups
    .map((g) => ({ ...g, plans: plans.filter((p) => p.enabled && p.group === g.id && scope.has(p.id)) }))
    .filter((g) => g.plans.length);
  const addonLines = cost.lines.filter((l) => l.kind === "addon");

  const setSeat = (key, qty) => {
    const next = { ...seats };
    if (qty > 0) next[key] = qty;
    else delete next[key];
    onSeatsChange(next);
  };

  const lineFor = (plan) =>
    lineByKey[plan.id] || {
      key: plan.id,
      name: plan.name,
      kind: plan.addon ? "suite" : "plan",
      unit: plan.price ? plan.price[plan.addon ? "teams" : priceMode] : null,
      estimated: !!plan.estimated,
      qty: 0,
      monthly: null,
      annual: null,
      priceLabel: plan.priceLabel || "",
    };

  const copyLink = async () => {
    const url = new URL(window.location.href);
    url.searchParams.set("seats", serializeSeats(seats));
    url.searchParams.set("region", pricing?.region || "us");
    try {
      await navigator.clipboard.writeText(url.toString());
      toast.success("Cost estimate link copied");
    } catch {
      toast(url.toString());
    }
  };

  const exportCsv = () => {
    downloadBlob(buildCostCSV(cost, pricing, priceMode), `license-cost-estimate-${(pricing?.currency || "usd").toLowerCase()}.csv`, "text/csv;charset=utf-8");
    toast.success("Cost estimate exported as CSV");
  };

  const hasSeats = cost.lines.length > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="lookup-sheet cost-sheet w-full sm:max-w-3xl p-0 flex flex-col" data-testid="cost-sheet">
        <SheetHeader className="lookup-head">
          <SheetTitle className="lookup-title">
            <Calculator size={18} aria-hidden="true" /> Cost calculator
          </SheetTitle>
          <SheetDescription>
            Enter seat counts per license. Totals use {pricing?.label || "United States"} list prices ({pricing?.currency || "USD"}),{" "}
            {priceMode === "teams" ? "With Teams" : "No Teams"} suite pricing.
          </SheetDescription>
        </SheetHeader>

        <div className="lookup-body cost-body">
          <div className="cost-head-row" aria-hidden="true">
            <span>License</span>
            <span className="num">Seats</span>
            <span className="num">Monthly</span>
            <span className="num">Annual</span>
            <span />
          </div>

          {sections.map((g) => (
            <section key={g.id} className="cost-section" data-testid={`cost-group-${g.id}`}>
              <h4>{g.tableLabel}</h4>
              <ul className="cost-lines">
                {g.plans.map((p) => (
                  <CostLine key={p.id} line={lineFor(p)} symbol={symbol} onChange={(q) => setSeat(p.id, q)} warning={warnByKey[p.id]} />
                ))}
              </ul>
            </section>
          ))}
          {sections.length === 0 && (
            <div className="lookup-empty">
              <Info size={16} /> Show at least one license in the matrix to price it here.
            </div>
          )}

          <section className="cost-section" data-testid="cost-group-addons">
            <h4>
              Add-on products <span className="muted">Copilot, Teams Premium, Intune Suite ...</span>
            </h4>
            {addonLines.length > 0 && (
              <ul className="cost-lines">
                {addonLines.map((l) => (
                  <CostLine key={l.key} line={l} symbol={symbol} onChange={(q) => setSeat(l.key, q)} onRemove={() => setSeat(l.key, 0)} />
                ))}
              </ul>
            )}
            <AddonPicker addons={addons} taken={new Set(addonLines.map((l) => l.name.toLowerCase()))} symbol={symbol} onPick={(name) => setSeat(addonKey(name), 1)} />
          </section>

          {cost.warnings.length > 0 && (
            <div className="cost-warnings" role="status" data-testid="cost-warnings">
              {cost.warnings.map((w) => (
                <div key={w.key} className="cost-warning">
                  <AlertTriangle size={14} aria-hidden="true" /> {w.text}
                </div>
              ))}
            </div>
          )}
        </div>

        <footer className="cost-footer" data-testid="cost-footer">
          <div className="cost-totals">
            <div className="cost-total">
              <span className="lbl">Seats</span>
              <span className="val mono" data-testid="cost-total-seats">
                {cost.seats.toLocaleString()}
              </span>
            </div>
            <div className="cost-total">
              <span className="lbl">Monthly</span>
              <span className="val mono" data-testid="cost-total-monthly">
                {formatPrice(cost.monthly, symbol, cost.estimated) || `${symbol}0.00`}
              </span>
            </div>
            <div className="cost-total primary">
              <span className="lbl">Annual</span>
              <span className="val mono" data-testid="cost-total-annual">
                {formatPrice(cost.annual, symbol, cost.estimated) || `${symbol}0.00`}
              </span>
            </div>
          </div>
          <div className="cost-notes">
            {cost.estimated && <span className="mini-chip est">{"\u2248"} includes estimated prices</span>}
            {cost.unpriced.length > 0 && <span className="mini-chip">{cost.unpriced.length} line(s) without list price excluded</span>}
          </div>
          <div className="lookup-actions">
            <button type="button" className="tb-btn sm" onClick={copyLink} disabled={!hasSeats} data-testid="cost-copy-link">
              <Link2 size={13} /> Copy link
            </button>
            <button type="button" className="tb-btn sm" onClick={exportCsv} disabled={!hasSeats} data-testid="cost-export-csv">
              <FileSpreadsheet size={13} /> CSV
            </button>
            <ShareMenu cardRef={cardRef} title={`cost-estimate-${(pricing?.currency || "usd").toLowerCase()}`} disabled={!hasSeats} />
            <button type="button" className="tb-btn sm ghost" onClick={() => onSeatsChange({})} disabled={!hasSeats} data-testid="cost-clear">
              <Eraser size={13} /> Clear
            </button>
          </div>
        </footer>
        {open && hasSeats && <QuoteCard cardRef={cardRef} cost={cost} pricing={pricing} priceMode={priceMode} />}
      </SheetContent>
    </Sheet>
  );
}
